import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import * as Mui from "@mui/material";
import DnsOutlined from "@mui/icons-material/DnsOutlined";
import FingerprintOutlined from "@mui/icons-material/FingerprintOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import WifiOutlined from "@mui/icons-material/WifiOutlined";
import WifiOffOutlined from "@mui/icons-material/WifiOffOutlined";
import moment from "moment";
import { APP_BAR_OFFSET, designColors, designFontFamily, designShell, designTheme, designType, paginationFooter, rowsPerPageOptions } from "services/constants/designTokens";
import { GlobalBreadcrumb, sharedStickyCustomFilterBarSx, sharedStickyFooterBarSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { DeviceStatusReportHeader } from "pages/TeamZone/TeamZoneHeaders";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import sessionHandling from "services/utils/notificationUtils";
import { mapMonitoringDevice, formatDeviceTime, parseDeviceTime, deviceHistoryEntries, monitoringDeviceStatusUrl } from "./deviceMonitoringData";
import { monitoringBodyCellSx, monitoringHeadCellSx, monitoringNameSx, monitoringRowSx, useMeasuredHeight, useMonitoringListAccess } from "./listUi";

const tones = { Online: "#059669", Offline: "#EF4444", "No Recent Punches": "#D97706" };
const cellText = (key, device) => ["lastSeen", "lastPunch"].includes(key) ? formatDeviceTime(device[key]) : device[key] ?? "—";
const DeviceStatus = ({ device }) => <RequestStatusChip status={device.status || "—"} colorCode={device.status_color || tones[device.status] || designColors.slate500} />;
const defaultHistoryRange = () => ({ startDate: moment().subtract(6, "days").format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD"), label: "Last 7 Days" });
const statusLast = headers => [
  ...headers.filter(header => header.key !== "status"),
  ...headers.filter(header => header.key === "status"),
];
const monitoringDeviceHeaders = statusLast(DeviceStatusReportHeader.filter(header => header.key !== "company_name"));
const DeviceIcon = ({ type, ...props }) => {
  const Icon = { attendance: FingerprintOutlined, patrolling: PlaceOutlined }[String(type ?? "").trim().toLowerCase()] || DnsOutlined;
  return <Icon {...props} />;
};

function MonitoringDevices() {
  const history = useHistory();
  const location = useLocation();
  const { domain, userDetails, subModuleId, canSaveViews } = useMonitoringListAccess("Devices");
  const { token } = useSelector(state => state.tokenReducer);
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [historyRange, setHistoryRange] = useState(defaultHistoryRange);
  const [statusHistory, setStatusHistory] = useState([]);
  const [historyLimited, setHistoryLimited] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyReloadKey, setHistoryReloadKey] = useState(0);
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);
  const { visibleHeaders: selectedHeaders, titleOverride } = useSavedViewSelector({
    domain, userId: userDetails?.id, subModuleId,
    storageKey: "monitoring_devices_status_saved_views",
    fullHeaders: monitoringDeviceHeaders,
    screenAccess: canSaveViews, onViewChange: resetPage,
  });
  const visibleHeaders = useMemo(() => statusLast(selectedHeaders), [selectedHeaders]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setDevices([]);
    setFilteredDevices(null);
    setSelected(null);
    const load = async () => {
      try {
        const records = [];
        const cursors = new Set();
        let afterId;
        let more;
        do {
          const response = await fetch(monitoringDeviceStatusUrl(domain, { afterId }), {
            headers: { "Content-Type": "application/json", Authorization: `token ${token}` },
            signal: controller.signal,
          });
          if (!active) return;
          if (response.status === 409) { sessionHandling(); throw new Error("Your session has expired."); }
          const result = await response.json();
          if (!active) return;
          if (!response.ok) throw new Error(result?.error || result?.detail || "Device status data could not be loaded.");
          if (!Array.isArray(result?.data)) throw new Error("Invalid device status response.");
          records.push(...result.data);
          more = result.has_more === true;
          if (more) {
            afterId = result.next_after_id;
            if (afterId == null || cursors.has(String(afterId)) || !result.data.length) throw new Error("Device pagination could not be completed.");
            cursors.add(String(afterId));
          }
        } while (more);
        const rows = [...new Map(records.map(row => [row.id ?? row.device_id, row])).values()].map(mapMonitoringDevice);
        setDevices(rows);
        setFilteredDevices(null);
        setPage(0);
        const deviceId = new URLSearchParams(location.search).get("device");
        if (deviceId) {
          setSelected(rows.find(device => String(device.device_id) === deviceId) || null);
          setHistoryRange(defaultHistoryRange());
        }
      } catch (failure) {
        if (!active) return;
        setDevices([]);
        setFilteredDevices(null);
        setError(failure.message || "Device status data could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; controller.abort(); };
  }, [domain, token, reloadKey, location.search]);

  const selectedDeviceId = selected?.device_id;
  useEffect(() => {
    if (selectedDeviceId == null) return undefined;
    let active = true;
    const controller = new AbortController();
    setHistoryLoading(true);
    setHistoryError("");
    setStatusHistory([]);
    setHistoryLimited(false);
    const loadHistory = async () => {
      try {
        const entries = [];
        const offsets = new Set();
        let historyOffset;
        let more;
        let detail;
        do {
          const response = await fetch(monitoringDeviceStatusUrl(domain, {
            deviceId: selectedDeviceId, ...historyRange, historyOffset,
          }), { headers: { "Content-Type": "application/json", Authorization: `token ${token}` }, signal: controller.signal });
          if (!active) return;
          if (response.status === 409) { sessionHandling(); throw new Error("Your session has expired."); }
          const result = await response.json();
          if (!active) return;
          if (!response.ok) throw new Error(result?.error || result?.detail || "Device details could not be loaded.");
          if (!result?.data || Array.isArray(result.data) || String(result.data.device_id) !== String(selectedDeviceId)) throw new Error("Invalid device detail response.");
          detail = result.data;
          const history = detail.status_history ?? [];
          if (!Array.isArray(history)) throw new Error("Invalid device history response.");
          entries.push(...history);
          setSelected(mapMonitoringDevice(detail));
          setHistoryLimited(result.history_limit_reached === true);
          more = result.history_has_more === true && result.history_limit_reached !== true;
          if (more) {
            historyOffset = result.next_history_offset;
            if (historyOffset == null || offsets.has(String(historyOffset)) || !history.length) throw new Error("Status history pagination could not be completed.");
            offsets.add(String(historyOffset));
          }
        } while (more);
        setStatusHistory(deviceHistoryEntries([{ ...detail, status_history: entries }], selectedDeviceId, historyRange.startDate, historyRange.endDate));
      } catch (failure) {
        if (active) setHistoryError(failure.message || "Status history could not be loaded.");
      } finally {
        if (active) setHistoryLoading(false);
      }
    };
    loadHistory();
    return () => { active = false; controller.abort(); };
  }, [domain, token, selectedDeviceId, historyRange, historyReloadKey]);

  const visibleDevices = filteredDevices ?? devices;
  const safePage = Math.min(page, Math.max(0, Math.ceil(visibleDevices.length / rowsPerPage) - 1));
  const renderCell = (key, device) => {
    if (key === "device_name") return <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <DeviceIcon type={device.type} fontSize="small" sx={{ color: designColors.slate500, flexShrink: 0 }} />
      <Mui.Typography noWrap sx={monitoringNameSx(selected?.device_id === device.device_id)}>{cellText(key, device)}</Mui.Typography>
    </Mui.Stack>;
    if (key === "status") return <DeviceStatus device={device} />;
    return cellText(key, device);
  };

  if (loading) return <Loader info="Loading..." />;
  return <Mui.ThemeProvider theme={designTheme}>
    <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
      <Mui.Box sx={{ ...designShell.pageSurface, fontFamily: designFontFamily, p: { xs: 2, md: 3 } }}>
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb trail={[{ label: "Monitoring", onClick: () => history.push("/admin/monitoring/dashboard") }, { label: "Devices" }]} />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>Device Monitoring</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>View the status and activity of biometric devices.</Mui.Typography>
          </Mui.Box>
          {error && <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setReloadKey(value => value + 1)}>Retry</Mui.Button>}>{error}</Mui.Alert>}
          <Mui.Box ref={customFilterRef} sx={{ ...designShell.sectionCard, ...sharedStickyCustomFilterBarSx }}>
            <CustomFilter title="devices" titleOverride={titleOverride} filterHeaders={visibleHeaders} data={devices} filteredData={visibleDevices} filteredDataReady={filteredDevices !== null} onFilteredDataChange={setFilteredDevices} screenAccess={true} hideCount labeledToolbar downloadFormats={["excel", "pdf", "csv"]} searchPlaceholder="Search by device or location..." />
          </Mui.Box>
          <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
            <Mui.TableContainer sx={{ width: "100%", overflowX: { xs: "auto", md: "visible" }, pb: footerHeight ? `${footerHeight}px` : 0 }}>
              <ApproveTable stickyHeader aria-label="Monitored biometric devices" sx={{ width: "100%", minWidth: 860, tableLayout: "fixed" }}>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    {visibleHeaders.map(header => <Mui.TableCell key={header.key} scope="col" title={header.name} sx={monitoringHeadCellSx(customFilterHeight)}>{header.name}</Mui.TableCell>)}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {visibleDevices.length === 0 ? <TableEmptyState dataLength={devices} colSpan={visibleHeaders.length} /> : visibleDevices.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage).map((device, index) => <Mui.TableRow key={`${device.device_id}-${index}`} onClick={() => { setSelected(device); setHistoryRange(defaultHistoryRange()); }} sx={monitoringRowSx(selected?.device_id === device.device_id)}>
                    {visibleHeaders.map(header => <Mui.TableCell key={header.key} title={String(cellText(header.key, device))} sx={monitoringBodyCellSx}>{renderCell(header.key, device)}</Mui.TableCell>)}
                  </Mui.TableRow>)}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({ page: safePage, rowsPerPage, total: visibleDevices.length, itemLabel: "devices", rowsPerPageOptions, onPageChange: setPage, onRowsPerPageChange: event => { setRowsPerPage(Number(event.target.value)); setPage(0); } })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Stack>
      </Mui.Box>
    </FilterProvider>
    <GlobalSidePanel open={Boolean(selected)} onClose={() => setSelected(null)} title="Device Details" hideExpand bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
      {selected && <Mui.Stack spacing={2.5}>
        <Mui.Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2, bgcolor: designColors.slateBg, borderRadius: 2 }}>
          <Mui.Avatar sx={{ bgcolor: "#DBEAFE", color: designColors.slate900, width: 52, height: 52 }}><DeviceIcon type={selected.type} sx={{ fontSize: 30 }} /></Mui.Avatar>
          <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
            <Mui.Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap"><Mui.Typography sx={designType.panelTitle}>{selected.device_name || "Device"}</Mui.Typography><DeviceStatus device={selected} /></Mui.Stack>
            <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>{selected.code} · {selected.type}</Mui.Typography>
          </Mui.Box>
        </Mui.Stack>
        <Mui.Stack spacing={2.5}>
          {[
            { label: "Location", value: selected.location ?? "—" },
            { label: "Device Type", value: selected.type },
            { label: "Serial Number", value: selected.serial_number || "—" },
            { label: "Last Seen", value: formatDeviceTime(selected.lastSeen), helper: selected.last_seen_available === false ? "Last communication time is unavailable" : "Last communication from this device" },
            { label: "Last Punch", value: formatDeviceTime(selected.lastPunch), helper: "Last punch received from this device" },
          ].map(field => <Mui.Box key={field.label} sx={{ display: "grid", gridTemplateColumns: "125px 1fr", gap: 2 }}>
            <Mui.Typography sx={{ fontSize: 14, color: designColors.slate600 }}>{field.label}</Mui.Typography>
            <Mui.Box><Mui.Typography sx={{ fontSize: 14, color: designColors.slate900 }}>{field.value}</Mui.Typography>{field.helper && <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>{field.helper}</Mui.Typography>}</Mui.Box>
          </Mui.Box>)}
        </Mui.Stack>
        <Mui.Alert severity={selected.status === "Online" ? "success" : selected.status === "Offline" ? "error" : "warning"} icon={selected.status === "Offline" ? <WifiOffOutlined /> : <WifiOutlined />} sx={{ borderRadius: 2 }}>
          <Mui.AlertTitle sx={{ fontWeight: 700 }}>{selected.status === "Online" ? "Device is Online" : selected.status === "Offline" ? "Device is Offline" : selected.status || "Status unavailable"}</Mui.AlertTitle>
          {selected.status_message || (selected.status === "Online" ? "Device is communicating normally." : selected.status === "Offline" ? "Device has stopped communicating." : selected.status === "No Recent Punches" ? "Device is communicating, but has no recent attendance punches." : "The latest device status returned by the server.")}
        </Mui.Alert>
        <Mui.Divider />
        <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Mui.Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>Status History</Mui.Typography>
          <ApprovalDateRangeFilter startDate={historyRange.startDate} endDate={historyRange.endDate} dateRangeLabel={historyRange.label} allowFutureDate={false} drops="up" popupTopOffset={APP_BAR_OFFSET} onChange={(start, end, label) => setHistoryRange({ startDate: moment(start).format("YYYY-MM-DD"), endDate: moment(end).format("YYYY-MM-DD"), label })} sx={{ maxWidth: 220, px: 1, "& .MuiTypography-root": { fontSize: 12 } }} />
        </Mui.Stack>
        {historyLimited && <Mui.Alert severity="info">Showing the available history. The server history limit has been reached.</Mui.Alert>}
        {historyLoading ? <Loader info="Loading..." minHeight="25vh" /> : historyError ? <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setHistoryReloadKey(value => value + 1)}>Retry</Mui.Button>}>{historyError}</Mui.Alert> : <Mui.TableContainer>
          <Mui.Table size="small" aria-label={`${selected.device_name} status history`}>
            <Mui.TableHead><Mui.TableRow><Mui.TableCell sx={designShell.tableHeadCellBase}>Date & Time</Mui.TableCell><Mui.TableCell sx={designShell.tableHeadCellBase}>Status</Mui.TableCell></Mui.TableRow></Mui.TableHead>
            <Mui.TableBody>
              {!statusHistory.length && <Mui.TableRow><Mui.TableCell colSpan={2} sx={{ py: 3, color: designColors.slate500 }}>No status changes in this period.</Mui.TableCell></Mui.TableRow>}
              {statusHistory.map((entry, index) => {
                const at = parseDeviceTime(entry.at);
                const date = at.format("YYYY-MM-DD");
                const previousDate = index ? parseDeviceTime(statusHistory[index - 1].at).format("YYYY-MM-DD") : null;
                const label = at.isSame(moment(), "day") ? "Today, " : at.isSame(moment().subtract(1, "day"), "day") ? "Yesterday, " : "";
                return <React.Fragment key={entry.id ?? `${entry.at}-${entry.status}`}>
                  {date !== previousDate && <Mui.TableRow><Mui.TableCell colSpan={2} sx={{ bgcolor: designColors.slateBg, fontWeight: 600, fontSize: 13 }}>{label}{at.format("DD MMM YYYY")}</Mui.TableCell></Mui.TableRow>}
                  <Mui.TableRow><Mui.TableCell sx={monitoringBodyCellSx}>{at.format("HH:mm")}</Mui.TableCell><Mui.TableCell sx={monitoringBodyCellSx}><DeviceStatus device={entry} /></Mui.TableCell></Mui.TableRow>
                </React.Fragment>;
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>}
      </Mui.Stack>}
    </GlobalSidePanel>
  </Mui.ThemeProvider>;
}

// Read-only screen: only the view permission applies.
export default withScreenPermissions(MonitoringDevices, SCREEN_ACCESS.monitoringDevices, "Device Monitoring");
